import { ActionType, getType } from 'typesafe-actions';

import { DEFAULT_ALERT_CONFIG } from '../enums';
import { AlertConfig, SpotRateDataPoint } from '../types';

import * as actions from './actions';
import {
  CurrencyResolutionWarning,
  PortfolioDataPoint,
  SimulationCubeFilters,
  SimulationMetadata,
  SimulationRunEstimate,
  SimulationRunStatus,
} from './types';

const DEFAULT_OUTPUT_CURRENCY_ID = 1;

export interface FLCashConsumptionState {
  portfolioData: {
    data: PortfolioDataPoint[];
    loading: boolean;
  };
  metadata: SimulationMetadata | null;
  filters: SimulationCubeFilters;
  tradeIdList: number[];
  spotRateData: Record<string, SpotRateDataPoint[]>;
  alertConfig: AlertConfig;
  selectedDate: string;
  simulationProgress: number | null;
  simulationRunStatus: SimulationRunStatus;
  simulationRunEstimate: SimulationRunEstimate | null;
  simulationRunEstimating: boolean;
  confirmationModalOpen: boolean;
  lastRequestPayload: SimulationCubeFilters | null;
  simulationRunStarting: boolean;
  simulationDownloading: boolean;
  outputCurrencyId: number;
  outputCurrencyResolving: boolean;
  outputCurrencyWarnings: CurrencyResolutionWarning[];
}

const initialState: FLCashConsumptionState = {
  portfolioData: {
    data: [],
    loading: false,
  },
  metadata: null,
  filters: {},
  tradeIdList: [],
  spotRateData: {},
  alertConfig: DEFAULT_ALERT_CONFIG,
  selectedDate: '',
  simulationProgress: null,
  simulationRunStatus: { status: null },
  simulationRunEstimate: null,
  simulationRunEstimating: false,
  confirmationModalOpen: false,
  lastRequestPayload: null,
  simulationRunStarting: false,
  simulationDownloading: false,
  outputCurrencyId: DEFAULT_OUTPUT_CURRENCY_ID,
  outputCurrencyResolving: false,
  outputCurrencyWarnings: [],
};

const flCashConsumptionReducer = (
  state: FLCashConsumptionState = initialState,
  action: ActionType<typeof actions>,
): FLCashConsumptionState => {
  switch (action.type) {
    case getType(actions.setSelectedDate):
      return { ...state, selectedDate: action.payload };

    case getType(actions.setAlertConfig):
      return { ...state, alertConfig: action.payload };

    case getType(actions.setSimulationProgress):
      return { ...state, simulationProgress: action.payload };

    case getType(actions.setSimulationRunStatus):
      return { ...state, simulationRunStatus: action.payload };

    case getType(actions.setSimulationRunEstimate):
      return { ...state, simulationRunEstimate: action.payload };

    case getType(actions.setSimulationRunEstimating):
      return { ...state, simulationRunEstimating: action.payload };

    case getType(actions.setConfirmationModalOpen):
      return { ...state, confirmationModalOpen: action.payload };

    case getType(actions.setLastRequestPayload):
      return { ...state, lastRequestPayload: action.payload };

    case getType(actions.setSimulationRunStarting):
      return { ...state, simulationRunStarting: action.payload };

    case getType(actions.setSimulationDownloading):
      return { ...state, simulationDownloading: action.payload };

    case getType(actions.setOutputCurrencyResolving):
      return { ...state, outputCurrencyResolving: action.payload };

    case getType(actions.setOutputCurrencyId):
      return {
        ...state,
        outputCurrencyId:
          action.payload.outputCurrencyId ?? DEFAULT_OUTPUT_CURRENCY_ID,
        outputCurrencyWarnings: action.payload.warningMessage
          ? [
              {
                code: 'custom_output_currency_selected',
                message: action.payload.warningMessage,
                details: {
                  output_currency_id:
                    action.payload.outputCurrencyId ??
                    DEFAULT_OUTPUT_CURRENCY_ID,
                },
              },
            ]
          : [],
      };

    case getType(actions.setOutputCurrencyResolution):
      return {
        ...state,
        outputCurrencyId:
          action.payload.outputCurrencyId ?? DEFAULT_OUTPUT_CURRENCY_ID,
        outputCurrencyWarnings: action.payload.warnings,
      };

    case getType(actions.clearRunState):
      return {
        ...state,
        simulationProgress: null,
        simulationRunStatus: { status: null },
        simulationRunEstimate: null,
        simulationRunEstimating: false,
        confirmationModalOpen: false,
        lastRequestPayload: null,
        simulationRunStarting: false,
        simulationDownloading: false,
      };

    case getType(actions.fetchSimulationCube.request):
      return {
        ...state,
        portfolioData: { ...state.portfolioData, loading: true },
        filters: action.payload ?? {},
        lastRequestPayload: action.payload ?? null,
      };

    case getType(actions.fetchSimulationCube.success):
      return {
        ...state,
        portfolioData: { data: action.payload.portfolioData, loading: false },
        metadata: action.payload.metadata,
        tradeIdList: action.payload.tradeIdList,
        spotRateData: action.payload.spotRateData,
        simulationRunStatus: action.payload.simulationRunStatus,
        simulationRunEstimate: null,
        simulationRunEstimating: false,
        confirmationModalOpen: false,
        simulationRunStarting: false,
        simulationProgress: null,
      };

    case getType(actions.fetchSimulationCube.failure):
      return {
        ...state,
        portfolioData: { ...state.portfolioData, loading: false },
        simulationRunStarting: false,
      };

    default:
      return state;
  }
};

export default flCashConsumptionReducer;
